import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Button from "../../../common/button/button.js";
import Text from "../../../common/typography/text.js";
import Panel from "../../../common/panel/index.js";
import DateRangePicker from "../../../common/date-picker-range/index.js";
import DrillBreadcrumb from "./DrillBreadcrumb.js";
import MeasureToggle from "./MeasureToggle.js";
import { getPrecedingPeriod } from "../aabiPeriods";
import "./../AABI.css";

const DATE_FORMAT = "YYYY-MM-DD";

// Presets (§5.3): an explicit Month-to-Date preset whose end is today — the
// generic "This Month" preset extends to month end and is not equivalent
// during an active month.
const buildPresets = () => {
  const today = dayjs();
  return [
    { label: "Today", value: [today, today] },
    { label: "Last 7 Days", value: [today.subtract(6, "day"), today] },
    { label: "Last 30 Days", value: [today.subtract(29, "day"), today] },
    { label: "Month to Date", value: [today.startOf("month"), today] },
    {
      label: "Last Month",
      value: [
        today.subtract(1, "month").startOf("month"),
        today.subtract(1, "month").endOf("month"),
      ],
    },
    { label: "Year to Date", value: [today.startOf("year"), today] },
  ];
};

const formatDateLabel = (dateStr) =>
  dayjs(dateStr, DATE_FORMAT).format("MMM D, YYYY");

// The analytics scope toolbar (§5.3): the command center for every
// period-sensitive section on the page. One date control, one Current/Realized
// control, one comparison label, one breadcrumb, one primary invoice action.
//
// The Executive Dashboard reuses it through additive props (Phase 2 §6.4);
// every default keeps the manufacturer page unchanged:
//   title / ariaLabel  the panel heading and its accessible name
//   presets            replaces the default date presets
//   extraControls      extra controls after the period picker (Region)
//   freshness          trailing slot before the actions (Updated + Refresh)
const AnalyticsToolbar = ({
  level,
  breadcrumbItems,
  startDate,
  endDate,
  measure,
  onMeasureChange,
  onDateRangeChange,
  onReset,
  showReset,
  viewInvoicesUrl,
  viewInvoicesLabel = "View Invoices",
  title = "Performance",
  ariaLabel = "Analytics scope and period",
  presets: presetsOverride,
  extraControls,
  freshness,
}) => {
  const defaultPresets = useMemo(buildPresets, []);
  const presets = presetsOverride ?? defaultPresets;

  const comparison = useMemo(() => {
    try {
      return getPrecedingPeriod({ startDate, endDate });
    } catch {
      return null;
    }
  }, [startDate, endDate]);

  const appliedRange = useMemo(
    () => [dayjs(startDate, DATE_FORMAT), dayjs(endDate, DATE_FORMAT)],
    [startDate, endDate],
  );

  return (
    <Panel
      contentClassName="aabi-toolbar"
      title={title}
      aria-label={ariaLabel}
    >
      <div className="aabi-toolbar-row">
        <div className="aabi-toolbar-period">
          <Text variant="tinyText" color="lightText">
            Period
          </Text>
          <DateRangePicker
            value={appliedRange}
            presets={presets.map(({ label, value }) => ({ label, value }))}
            format={DATE_FORMAT}
            allowClear={false}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                onDateRangeChange({
                  startDate: dates[0].format(DATE_FORMAT),
                  endDate: dates[1].format(DATE_FORMAT),
                });
              }
            }}
          />

          {extraControls}

          <MeasureToggle measure={measure} onChange={onMeasureChange} />

          {comparison && (
            <Text variant="tinyText" color="lightText" className="aabi-comparison-label">
              {`Compared with ${formatDateLabel(comparison.compareStartDate)} - ${formatDateLabel(comparison.compareEndDate)}`}
            </Text>
          )}
        </div>

        <div className="aabi-toolbar-actions">
          {freshness}
          {showReset && (
            <Button variant="link" onClick={onReset}>
              <Text variant="smallText" color="primaryColor">
                Reset filters
              </Text>
            </Button>
          )}
          {viewInvoicesUrl && (
            <Link to={viewInvoicesUrl} className="aabi-view-invoices-link">
              <Button variant="primary">{viewInvoicesLabel}</Button>
            </Link>
          )}
        </div>
      </div>

      {breadcrumbItems?.length > 1 && (
        <div className="aabi-toolbar-breadcrumb-row">
          <DrillBreadcrumb items={breadcrumbItems} />
        </div>
      )}
    </Panel>
  );
};

export default AnalyticsToolbar;
